/**
 * @fileoverview Arama Uyari ve Bildirim Fonksiyonlari
 *
 * Bu modul, kullanici arama davranislarina dayali akilli uyarilari
 * ve ChatGPT'ye arama sonucu bildirimlerini yonetir.
 *
 * @module hooks/useSearchAlerts
 */

import { calculatePriceStats, isSendMessageAvailable } from "../utils";
import { parseSearchPath, extractFilterStats } from "../utils/parsing";

/**
 * Arama kaliplarina dayali akilli uyarilar olusturur.
 *
 * Kullanicinin arama davranislarini analiz ederek faydali ipuclari saglar:
 * - Cok fazla filtre kullanimi (5+ filtre)
 * - Ayni bolgede tekrarli aramalar (3+ kez)
 * - Butce daralma uyarisi
 *
 * @function buildSearchAlerts
 * @param {string} path - Arama yolu
 * @param {string} filters - Filtre string'i
 * @param {Array} searchHistory - Onceki arama gecmisi
 * @returns {string[]} Uyari mesajlari dizisi
 *
 * @example
 * const alerts = buildSearchAlerts("satilik-konut/istanbul-kadikoy", "oda-sayisi=3-1", searchHistory);
 * // Returns: ["kadikoy icin 3. arama, komsu ilcelere de bak"]
 */
export function buildSearchAlerts(path, filters, searchHistory) {
  const alerts = [];
  const locationInfo = parseSearchPath(path);
  const { parsed: filterInfo, count: filterCount } = extractFilterStats(filters);

  // 1. Cok fazla filtre (5+)
  if (filterCount >= 5) {
    alerts.push(`${filterCount} filtre aktif, sonuc az olabilir`);
  }

  // 2. Ayni bolgede cok arama (3+ kez ayni ilce)
  if (locationInfo?.ilce && Array.isArray(searchHistory)) {
    const sameDistrictCount = searchHistory.filter((h) => {
      const hLoc = parseSearchPath(h?.path);
      return hLoc?.ilce === locationInfo.ilce;
    }).length;

    if (sameDistrictCount >= 3) {
      alerts.push(`${locationInfo.ilce} icin ${sameDistrictCount}. arama, komsu ilcelere de bak`);
    }
  }

  // 3. Butce sikistirma (onceki aramadan daha dar fiyat araligi)
  if (filterInfo.fiyatMax && Array.isArray(searchHistory) && searchHistory.length > 0) {
    const prevFilters = extractFilterStats(searchHistory[0]?.filters);
    if (prevFilters.parsed.fiyatMax && filterInfo.fiyatMax < prevFilters.parsed.fiyatMax) {
      alerts.push("Butceyi daraltin, secenekler azalabilir");
    }
  }

  return alerts;
}

/**
 * ChatGPT'ye arama sonuclari hakkinda bilgi gonderir.
 *
 * Arama tamamlandiginda `window.openai.sendFollowUpMessage` API'si ile
 * ChatGPT'ye arama ozeti ve istatistiklerini gonderir. ChatGPT bu bilgileri
 * kullanarak kullaniciya yardimci oneriler sunabilir.
 *
 * @function notifySearchResults
 * @param {Object} structured - Yapilandirilmis arama sonucu
 * @param {string} path - Arama yolu
 * @param {string} filters - Filtre string'i
 * @param {Array} searchHistory - Arama gecmisi
 * @returns {void}
 *
 * @example
 * notifySearchResults(structured, "satilik-konut/istanbul", "fiyat-max=5000000", searchHistory);
 */
export function notifySearchResults(structured, path, filters, searchHistory) {
  if (!isSendMessageAvailable()) return;

  const total = structured.total || 0;
  const listings = structured.listings || [];
  const locationInfo = parseSearchPath(path);
  const { parsed: filterInfo } = extractFilterStats(filters);

  // Calculate price stats from listings
  const priceStats = calculatePriceStats(listings);

  const summary = {
    konum: locationInfo,
    filtreler: Object.keys(filterInfo).length > 0 ? filterInfo : null,
    sonuc: {
      toplamIlan: total,
      gosterilenIlan: listings.length,
    },
    fiyatIstatistik: priceStats,
  };

  // Build smart alerts
  const alerts = buildSearchAlerts(path, filters, searchHistory);

  let prompt;
  if (total === 0) {
    prompt = `Bu kriterlerde ilan bulamadim, ne yapmaliyim? ${JSON.stringify(summary)}`;
  } else {
    prompt = `Su aramayi yaptim, kisaca ozetle: ${JSON.stringify(summary)}`;
  }

  if (alerts.length > 0) {
    prompt += ` [IPUCU: ${alerts.join(", ")}]`;
  }

  window.openai.sendFollowUpMessage({ prompt });
}
